import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { experience } from "../../data/content";
import { Reveal, SectionHeading } from "./Reveal";

const easing = [0.22, 1, 0.36, 1];

const Role = ({ role, i }) => (
  <Reveal delay={i * 0.05}>
    <div
      data-testid={`experience-card-${i}`}
      className="group relative rounded-3xl border border-[var(--border-subtle)] bg-surface p-7 md:p-10 transition-all duration-300 hover:border-[var(--border-strong)] hover:bg-surface-elevated"
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight text-primary-c">
              {role.company}
            </h3>
            {role.current && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] bg-white/[0.03] px-2.5 py-0.5 text-[11px] font-medium text-secondary-c">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: "var(--status-available)" }}
                />
                Current
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm md:text-base text-secondary-c">
            {role.role} · {role.location}
          </p>
        </div>
        <span className="font-mono-tech text-xs tracking-widest text-tertiary-c whitespace-nowrap">
          {role.period}
        </span>
      </div>

      <p className="text-base md:text-lg text-primary-c leading-relaxed mb-6">
        {role.summary}
      </p>

      <div className="grid md:grid-cols-2 gap-6 md:gap-10">
        <div>
          <p className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-tertiary-c mb-3">
            The problem
          </p>
          <p className="text-sm md:text-[15px] text-secondary-c leading-relaxed">
            {role.problem}
          </p>
        </div>
        <div>
          <p className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-tertiary-c mb-3">
            What I built
          </p>
          <ul className="space-y-2.5">
            {role.approach.map((a, idx) => (
              <li
                key={idx}
                className="flex gap-2.5 text-sm md:text-[15px] text-secondary-c leading-relaxed"
              >
                <ArrowRight
                  className="w-4 h-4 mt-0.5 shrink-0 text-tertiary-c"
                  strokeWidth={1.5}
                />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-[var(--border-subtle)]">
        <p className="text-sm md:text-[15px] text-primary-c leading-relaxed">
          <span className="text-tertiary-c">Outcome — </span>
          {role.impact}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {role.stack.map((s) => (
            <span
              key={s}
              className="font-mono-tech text-[11px] tracking-wide bg-white/[0.04] border border-white/10 text-secondary-c rounded-full px-2.5 py-1"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  </Reveal>
);

export const Experience = () => (
  <section
    id="experience"
    data-testid="experience-section"
    className="relative max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32"
  >
    <SectionHeading
      index="02"
      overline="Experience"
      title="Roles as engineering case studies."
      description="Not a list of responsibilities — the problem each role posed, how I approached the architecture, and what changed for the business."
    />
    <motion.div
      initial="hidden"
      className="flex flex-col gap-5 md:gap-6"
    >
      {experience.map((role, i) => (
        <Role key={role.company} role={role} i={i} />
      ))}
    </motion.div>
  </section>
);
